import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { useSignedImageSrc } from '@/hooks/useSignedImageSrc';
import { useManagedViewImageSrc, isLoadableImgSrc } from '@/hooks/useManagedViewImageSrc';
import { pickImageSignOptions, refreshSignedDisplayUrl, appendViewVariant } from '@/utils/imageDisplayAccess';
import { isViewLoadCooldownActive, waitForViewLoadCooldown } from '@/utils/viewLoadRateLimitGuard';
import '@/styles/shared/ipe-lazy-load.css';

function mergeRefs(ref, nodeRef) {
  return (node) => {
    nodeRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };
}

/**
 * 展示受管存储图片（local/oss 统一）：签名 + 视口懒加载 + view 并发队列，避免 429 裂图。
 * 未拿到可加载 src 前只渲染占位，避免空 src 的 img 露出 alt/裂图。
 */
const SignedImage = forwardRef(function SignedImage({
  url,
  displayUrl,
  viewUrl,
  storedUrlDisplay,
  storedViewUrl,
  // pickImageSignOptions / 调用方 spread 进来的字段，勿落到 DOM
  urlDisplay,
  primaryImageUrlDisplay,
  primaryImageViewUrl,
  initialImageUrlDisplay,
  finalImageUrlDisplay,
  outputImageUrlDisplay,
  inputImageUrlDisplay,
  outputVideoUrlDisplay,
  videoUrlDisplay,
  outputVideoPosterUrlDisplay,
  previewDisplayUrl,
  previewViewUrl,
  alt = '',
  fallback = null,
  loadingFallback = null,
  record = null,
  loading = 'lazy',
  variant = 'full',
  viewRoot = null,
  viewRootMargin,
  decoding = 'async',
  onLoad: onLoadProp,
  onError: onErrorProp,
  className,
  style,
  ...imgProps
}, ref) {
  const signOptions = {
    ...pickImageSignOptions(record),
    imageId: record?.id ?? record?.imageId,
    displayUrl: displayUrl ?? record?.displayUrl,
    viewUrl: viewUrl ?? record?.viewUrl,
    storedUrlDisplay: storedUrlDisplay ?? record?.storedUrlDisplay,
    storedViewUrl: storedViewUrl ?? record?.storedViewUrl,
    urlDisplay: urlDisplay ?? record?.urlDisplay,
    primaryImageUrlDisplay: primaryImageUrlDisplay ?? record?.primaryImageUrlDisplay,
    primaryImageViewUrl: primaryImageViewUrl ?? record?.primaryImageViewUrl,
    initialImageUrlDisplay: initialImageUrlDisplay ?? record?.initialImageUrlDisplay,
    finalImageUrlDisplay: finalImageUrlDisplay ?? record?.finalImageUrlDisplay,
    outputImageUrlDisplay: outputImageUrlDisplay ?? record?.outputImageUrlDisplay,
    inputImageUrlDisplay: inputImageUrlDisplay ?? record?.inputImageUrlDisplay,
    outputVideoUrlDisplay: outputVideoUrlDisplay ?? record?.outputVideoUrlDisplay,
    videoUrlDisplay: videoUrlDisplay ?? record?.videoUrlDisplay,
    outputVideoPosterUrlDisplay: outputVideoPosterUrlDisplay ?? record?.outputVideoPosterUrlDisplay,
    previewDisplayUrl: previewDisplayUrl ?? record?.previewDisplayUrl,
    previewViewUrl: previewViewUrl ?? record?.previewViewUrl,
    variant,
  };
  const signedSrc = useSignedImageSrc(url, signOptions);
  const [refreshedSrc, setRefreshedSrc] = useState('');
  const [loadFailed, setLoadFailed] = useState(false);
  const refreshInFlightRef = useRef(false);
  const lastErrorRefreshAtRef = useRef(0);
  /** 同一 url 生命周期内最多续签次数，避免 429 冷却下 wait→refresh→onError 无限循环 */
  const refreshAttemptRef = useRef(0);
  const MAX_ERROR_REFRESH_ATTEMPTS = 1;
  const effectiveSignedSrc = appendViewVariant(refreshedSrc || signedSrc, variant);

  useEffect(() => {
    setRefreshedSrc('');
    setLoadFailed(false);
    refreshInFlightRef.current = false;
    refreshAttemptRef.current = 0;
  }, [url, signOptions.displayUrl, signOptions.viewUrl, signOptions.storedUrlDisplay, signOptions.storedViewUrl, variant]);

  const eagerView = loading === 'eager';
  const {
    src: queuedSrc,
    elementRef,
    onLoad: onQueueLoad,
    onError: onQueueError,
  } = useManagedViewImageSrc(effectiveSignedSrc, {
    eager: eagerView,
    root: viewRoot,
    rootMargin: viewRootMargin,
  });

  const handleLoad = useCallback((event) => {
    setLoadFailed(false);
    onQueueLoad();
    onLoadProp?.(event);
  }, [onLoadProp, onQueueLoad]);

  const handleError = useCallback((event) => {
    onQueueError();
    // 须在 await 之前同步调用：React 合成事件在异步后会清空 currentTarget。
    onErrorProp?.(event);
    if (refreshAttemptRef.current >= MAX_ERROR_REFRESH_ATTEMPTS) {
      setLoadFailed(true);
      return;
    }
    const now = Date.now();
    const cooldownUrl = effectiveSignedSrc || url || signOptions.displayUrl || signOptions.viewUrl;
    const startRefresh = () => {
      if (refreshInFlightRef.current || !(url || signOptions.displayUrl || signOptions.viewUrl)) {
        setLoadFailed(true);
        return;
      }
      refreshInFlightRef.current = true;
      refreshAttemptRef.current += 1;
      lastErrorRefreshAtRef.current = now;
      void (async () => {
        try {
          await waitForViewLoadCooldown(cooldownUrl);
          const next = await refreshSignedDisplayUrl(url, signOptions);
          if (next) {
            setLoadFailed(false);
            setRefreshedSrc(next);
          } else {
            setLoadFailed(true);
          }
        } catch {
          setLoadFailed(true);
        } finally {
          refreshInFlightRef.current = false;
        }
      })();
    };

    // 限流期间：静默等「本域」冷却后再续签一次，不弹提示，保持 FIFO 队列秩序
    if (isViewLoadCooldownActive(cooldownUrl) || now - lastErrorRefreshAtRef.current < 3000) {
      if (isViewLoadCooldownActive(cooldownUrl)) {
        startRefresh();
      } else {
        setLoadFailed(true);
      }
      return;
    }
    startRefresh();
  }, [onErrorProp, onQueueError, url, signOptions, effectiveSignedSrc]);

  const hasInput = url || signOptions.displayUrl || signOptions.viewUrl;
  if (!hasInput) return fallback;

  const loadableSigned = effectiveSignedSrc && isLoadableImgSrc(effectiveSignedSrc);
  const waitingForSign = Boolean(hasInput) && !loadableSigned;
  const waitingForLoad = loadableSigned && !queuedSrc;

  const renderShell = (child) => {
    const shellClassName = ['ipe-lazy-image', className].filter(Boolean).join(' ');
    return (
      <span
        ref={mergeRefs(ref, elementRef)}
        className={shellClassName}
        style={style}
      >
        {child}
      </span>
    );
  };

  // 读图失败：展示 fallback/占位，避免浏览器默认裂图 + alt 文件名铺满格子
  if (loadFailed) {
    if (fallback && typeof fallback === 'string' && isLoadableImgSrc(fallback) && fallback !== queuedSrc) {
      return (
        <img
          ref={mergeRefs(ref, elementRef)}
          src={fallback}
          alt=""
          className={className}
          style={style}
          decoding={decoding}
          loading="eager"
        />
      );
    }
    if (fallback && React.isValidElement(fallback)) {
      return renderShell(fallback);
    }
    return renderShell(<span className="ipe-signed-image-placeholder" aria-hidden="true" />);
  }

  if (waitingForSign || waitingForLoad) {
    if (loadingFallback && React.isValidElement(loadingFallback)) {
      return renderShell(loadingFallback);
    }
    if (waitingForSign && fallback) {
      if (typeof fallback === 'string' && isLoadableImgSrc(fallback)) {
        return (
          <img
            ref={mergeRefs(ref, elementRef)}
            src={fallback}
            alt=""
            className={className}
            style={style}
            decoding={decoding}
            loading="eager"
          />
        );
      }
      if (React.isValidElement(fallback)) return fallback;
    }
    return renderShell(<span className="ipe-signed-image-placeholder" aria-hidden="true" />);
  }

  return (
    <img
      ref={mergeRefs(ref, elementRef)}
      src={queuedSrc}
      alt={alt}
      className={className}
      style={style}
      decoding={decoding}
      onLoad={handleLoad}
      onError={handleError}
      {...imgProps}
      loading="eager"
    />
  );
});

export default SignedImage;
